import { Link } from "@tanstack/react-router";
import { Check, ChevronRight, X } from "lucide-react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { JsonLd } from "./JsonLd";
import { breadcrumbSchema, faqPageSchema } from "@/lib/schemas";
import type { ComparisonData } from "@/lib/comparisons-data";

function Cell({ data }: { data: string | { value: string; highlight?: boolean } }) {
  const value = typeof data === "string" ? data : data.value;
  const highlight = typeof data === "object" && data.highlight;
  return (
    <td
      className={`px-4 py-3 text-sm ${
        highlight ? "bg-primary/5 font-semibold text-foreground" : "text-muted-foreground"
      }`}
    >
      {value}
    </td>
  );
}

interface ComparisonLayoutProps {
  data: ComparisonData;
}

export function ComparisonLayout({ data }: ComparisonLayoutProps) {
  // group rows by category
  const grouped: Record<string, typeof data.rows> = {};
  data.rows.forEach((r) => {
    const cat = r.category ?? "Inne";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(r);
  });

  const breadcrumbs = [
    { name: "Strona główna", path: "/" },
    { name: "Porównania", path: "/porownanie" },
    { name: data.title, path: `/porownanie/${data.slug}` },
  ];

  return (
    <div className="min-h-screen bg-background">
      <JsonLd data={breadcrumbSchema(breadcrumbs)} />
      <JsonLd data={faqPageSchema(data.faq)} />
      <Header />

      <main className="mx-auto max-w-6xl px-6 pb-24 pt-32">
        {/* Breadcrumbs */}
        <nav className="mb-8 flex items-center gap-2 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/porownanie" className="hover:text-foreground">Porównania</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="truncate text-foreground">{data.platformA.name} vs {data.platformB.name}</span>
        </nav>

        <header className="mb-12 max-w-4xl">
          <span className="inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            Porównanie
          </span>
          <h1 className="mt-4 font-display text-3xl font-bold md:text-5xl">{data.title}</h1>
          <p className="mt-4 text-lg text-muted-foreground">{data.intro}</p>
        </header>

        {/* Quick cards */}
        <div className="grid gap-4 md:grid-cols-2">
          {[data.platformA, data.platformB].map((p, i) => (
            <div
              key={p.name}
              className="rounded-2xl border border-border bg-card p-6 transition hover:border-primary"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Opcja {i === 0 ? "A" : "B"}
              </p>
              <h2 className="mt-2 font-display text-2xl font-bold">{p.name}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
              <p className="mt-4 text-sm">
                <span className="font-semibold text-foreground">Najlepsze dla:</span>{" "}
                <span className="text-muted-foreground">{p.bestFor}</span>
              </p>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        <section className="mt-12">
          <h2 className="mb-4 font-display text-2xl font-bold">Pełne porównanie funkcji</h2>
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full border-collapse text-left">
              <thead className="bg-card">
                <tr className="border-b border-border">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Funkcja
                  </th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-foreground">
                    {data.platformA.name}
                  </th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-foreground">
                    {data.platformB.name}
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([category, rows]) => (
                  <>
                    <tr key={`cat-${category}`} className="bg-primary/5">
                      <td colSpan={3} className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-primary">
                        {category}
                      </td>
                    </tr>
                    {rows.map((r, j) => (
                      <tr key={`${category}-${j}`} className="border-b border-border/60">
                        <td className="px-4 py-3 text-sm font-medium">{r.feature}</td>
                        <Cell data={r.a} />
                        <Cell data={r.b} />
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Verdict */}
        <section className="mt-16">
          <h2 className="font-display text-2xl font-bold md:text-3xl">{data.verdict.headline}</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-bold">Wybierz {data.platformA.name}, gdy:</h3>
              <ul className="mt-3 space-y-2">
                {data.verdict.whenA.map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-bold">Wybierz {data.platformB.name}, gdy:</h3>
              <ul className="mt-3 space-y-2">
                {data.verdict.whenB.map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {data.verdict.whenBoth && data.verdict.whenBoth.length > 0 && (
            <div className="mt-4 rounded-2xl border border-primary/30 bg-primary/5 p-6">
              <h3 className="font-display text-lg font-bold">Możesz potrzebować obu, gdy:</h3>
              <ul className="mt-3 space-y-2">
                {data.verdict.whenBoth.map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* FAQ */}
        <section className="mt-16">
          <h2 className="font-display text-2xl font-bold md:text-3xl">Najczęstsze pytania</h2>
          <div className="mt-6 space-y-3">
            {data.faq.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl border border-border bg-card p-5 open:border-primary"
              >
                <summary className="cursor-pointer list-none text-base font-semibold">
                  <span className="flex items-center justify-between gap-4">
                    {f.q}
                    <ChevronRight className="h-4 w-4 shrink-0 transition group-open:rotate-90" />
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-16 overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-background to-primary-glow/10 p-8 text-center">
          <h3 className="font-display text-2xl font-bold md:text-3xl">
            Nie wiesz co wybrać dla swojego sklepu?
          </h3>
          <p className="mt-3 text-muted-foreground">
            30 minut bezpłatnej konsultacji — zadam właściwe pytania i podam konkretną rekomendację.
          </p>
          <a
            href="/#kontakt"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-primary to-primary-glow px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow transition hover:scale-[1.03]"
          >
            Umów konsultację
            <ChevronRight className="h-4 w-4" />
          </a>
        </section>
      </main>

      <Footer />
    </div>
  );
}
